import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

function publicClient() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

// Public column allowlists — internal editorial workflow fields
// (submitted_at / reviewed_at / reviewed_by / review_notes) are never exposed
// to anonymous readers and are revoked at the database level too.
const DOMAIN_PUBLIC_COLUMNS =
  "id, slug, name, tagline, description, icon, color, is_published, sort_order, created_at, updated_at, engine_key, taxonomy, tier, status";
const PATH_PUBLIC_COLUMNS =
  "id, domain_id, slug, title, subtitle, description, cover_url, difficulty, tier, est_minutes, is_published, published_at, sort_order, created_at, updated_at, status";
const LESSON_PUBLIC_COLUMNS =
  "id, domain_id, category_id, slug, title, subtitle, summary, body_md, hero_image_url, media, difficulty, tier, est_minutes, tags, refs, prerequisites, related_ids, quiz, prompts, is_published, published_at, created_at, updated_at, status, learning_objectives, sections, historical_context, scientific_context, traditional_context, kaf_original, applications, assessment, next_lesson_slugs, word_count, provenance, mastery_rule";

export const listDomains = createServerFn({ method: "GET" }).handler(async () => {
  const supa = publicClient();
  const { data, error } = await supa
    .from("academy_domains")
    .select("id, slug, name, tagline, description, icon, color, sort_order")
    .eq("is_published", true)
    .order("sort_order");
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const getDomain = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }) => {
    const supa = publicClient();
    const { data: domain } = await supa
      .from("academy_domains")
      .select(DOMAIN_PUBLIC_COLUMNS)
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (!domain) return null;
    const [paths, nodes, certifications] = await Promise.all([
      supa.from("academy_paths").select("id, slug, title, subtitle, description, difficulty, tier, est_minutes, sort_order")
        .eq("domain_id", domain.id).eq("is_published", true).order("sort_order"),
      supa.from("academy_interactive_nodes")
        .select("id, kind, key, label, description, payload, lesson_id, sort_order")
        .eq("domain_id", domain.id).eq("is_published", true).order("sort_order"),
      supa.from("academy_certifications")
        .select("id, slug, title, description, entitlement_slug")
        .eq("domain_id", domain.id).eq("is_published", true),
    ]);
    return {
      domain,
      paths: paths.data ?? [],
      nodes: nodes.data ?? [],
      certifications: certifications.data ?? [],
    };
  });

export const getInteractiveMap = createServerFn({ method: "GET" })
  .inputValidator((d: { domainSlug: string }) => d)
  .handler(async ({ data }) => {
    const supa = publicClient();
    const { data: domain } = await supa.from("academy_domains").select("id, engine_key").eq("slug", data.domainSlug).maybeSingle();
    if (!domain) return null;
    const { data: nodes } = await supa.from("academy_interactive_nodes")
      .select("id, kind, key, label, description, payload, lesson_id, sort_order")
      .eq("domain_id", domain.id).eq("is_published", true).order("sort_order");
    return { engineKey: domain.engine_key, nodes: nodes ?? [] };
  });

export const getLessonBlocks = createServerFn({ method: "GET" })
  .inputValidator((d: { lessonId: string }) => d)
  .handler(async ({ data }) => {
    const supa = publicClient();
    const { data: rows } = await supa.from("academy_lesson_blocks")
      .select("id, sort_order, block_type, data, entitlement_slug")
      .eq("lesson_id", data.lessonId).eq("is_published", true).order("sort_order");
    return rows ?? [];
  });

export const listCertifications = createServerFn({ method: "GET" })
  .inputValidator((d: { domainSlug?: string } = {}) => d)
  .handler(async ({ data }) => {
    const supa = publicClient();
    let q = supa.from("academy_certifications")
      .select("id, slug, title, description, criteria, entitlement_slug, domain_id")
      .eq("is_published", true);
    if (data?.domainSlug) {
      const { data: d } = await supa.from("academy_domains").select("id").eq("slug", data.domainSlug).maybeSingle();
      if (d) q = q.eq("domain_id", d.id);
    }
    const { data: rows } = await q;
    return rows ?? [];
  });

export const evaluateCertification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { certificationId: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: cert } = await context.supabase.from("academy_certifications")
      .select("id, slug, title, criteria, domain_id").eq("id", data.certificationId).maybeSingle();
    if (!cert) throw new Error("Certification not found");
    const criteria = (cert.criteria ?? {}) as {
      lesson_ids?: string[]; path_ids?: string[]; quiz_min_pct?: number;
    };
    const requiredLessonIds = new Set<string>(criteria.lesson_ids ?? []);
    if (criteria.path_ids?.length) {
      const { data: links } = await context.supabase.from("academy_path_lessons")
        .select("lesson_id").in("path_id", criteria.path_ids);
      for (const l of links ?? []) requiredLessonIds.add(l.lesson_id);
    }
    if (!requiredLessonIds.size) return { passed: false, missing: [], reason: "No criteria defined" };
    const { data: progress } = await context.supabase.from("academy_progress")
      .select("lesson_id, status, mastery_pct").eq("user_id", context.userId)
      .in("lesson_id", Array.from(requiredLessonIds));
    const minPct = criteria.quiz_min_pct ?? 0;
    const doneIds = new Set(
      (progress ?? []).filter((p) => p.status === "completed" && (p.mastery_pct ?? 0) >= minPct)
        .map((p) => p.lesson_id),
    );
    const missing = Array.from(requiredLessonIds).filter((id) => !doneIds.has(id));
    if (missing.length) return { passed: false, missing };
    // Issue certificate through a server-only SECURITY DEFINER function that
    // re-verifies the certification criteria. The RPC is not executable by
    // anon/authenticated — only the trusted server may call it, for the
    // already-authenticated caller.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: verifyCode, error: issueError } = await (supabaseAdmin as any).rpc(
      "issue_academy_certificate_for",
      { _user: context.userId, _certification_id: data.certificationId },
    );
    if (issueError) throw new Error(issueError.message);
    return { passed: true, verifyCode };

  });

export const checkContentEntitlement = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { domainId?: string; pathId?: string; lessonId?: string }) => d)
  .handler(async ({ data, context }) => {
    const filters: string[] = [];
    if (data.domainId) filters.push(`domain_id.eq.${data.domainId}`);
    if (data.pathId) filters.push(`path_id.eq.${data.pathId}`);
    if (data.lessonId) filters.push(`lesson_id.eq.${data.lessonId}`);
    if (!filters.length) return { locked: false };
    // The entitlement map is admin-only at the RLS layer (it reveals the full
    // paywall topology). Read it server-side after the caller is authenticated,
    // and return only the lock decision.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rules } = await (supabaseAdmin as any).from("academy_entitlement_map")
      .select("product_slug, requires_subscription").or(filters.join(","));

    if (!rules || !rules.length) return { locked: false };
    for (const r of rules) {
      if (r.requires_subscription) {
        const { data: sub } = await context.supabase.rpc("has_any_active_subscription", { _user_id: context.userId });
        if (sub) return { locked: false };
      }
      if (r.product_slug) {
        const { data: ok } = await context.supabase.rpc("has_entitlement", {
          _user_id: context.userId, _product_slug: r.product_slug,
        });
        if (ok) return { locked: false };
      }
    }
    return { locked: true };
  });

type PathLessonRow = {
  id: string; slug: string; title: string; subtitle: string | null;
  summary: string | null; difficulty: string; tier: string; est_minutes: number;
};

export const getPath = createServerFn({ method: "GET" })
  .inputValidator((d: { domainSlug: string; pathSlug: string }) => d)
  .handler(async ({ data }) => {
    const supa = publicClient();
    const { data: domain } = await supa.from("academy_domains").select("id").eq("slug", data.domainSlug).maybeSingle();
    if (!domain) return null;
    const { data: path } = await supa.from("academy_paths").select(PATH_PUBLIC_COLUMNS)
      .eq("domain_id", domain.id).eq("slug", data.pathSlug).eq("is_published", true).maybeSingle();
    if (!path) return null;
    const { data: links } = await supa.from("academy_path_lessons").select("ord, lesson_id").eq("path_id", path.id).order("ord");
    const ids = (links ?? []).map((l) => l.lesson_id);
    let lessonRows: PathLessonRow[] = [];
    if (ids.length) {
      const { data: ls } = await supa.from("academy_lessons")
        .select("id, slug, title, subtitle, summary, difficulty, tier, est_minutes").in("id", ids);
      lessonRows = (ls ?? []) as PathLessonRow[];
    }
    const byId = new Map(lessonRows.map((l) => [l.id, l]));
    const ordered: PathLessonRow[] = (links ?? [])
      .map((l) => byId.get(l.lesson_id))
      .filter((x): x is PathLessonRow => Boolean(x));
    return { path, lessons: ordered };
  });

export const getLesson = createServerFn({ method: "GET" })
  .inputValidator((d: { domainSlug: string; lessonSlug: string }) => d)
  .handler(async ({ data }) => {
    const supa = publicClient();
    const { data: domain } = await supa.from("academy_domains").select("id").eq("slug", data.domainSlug).maybeSingle();
    if (!domain) return null;
    const { data: lesson, error } = await supa.from("academy_lessons").select(LESSON_PUBLIC_COLUMNS)
      .eq("domain_id", domain.id).eq("slug", data.lessonSlug).eq("is_published", true).maybeSingle();
    if (error) throw new Error(error.message);
    return lesson;
  });

export const searchLessons = createServerFn({ method: "GET" })
  .inputValidator((d: { q: string; domainSlug?: string }) => d)
  .handler(async ({ data }) => {
    const supa = publicClient();
    let q = supa.from("academy_lessons")
      .select("id, slug, title, summary, difficulty, tier, domain_id, est_minutes")
      .eq("is_published", true).limit(30);
    if (data.q.trim()) q = q.ilike("title", `%${data.q}%`);
    if (data.domainSlug) {
      const { data: d } = await supa.from("academy_domains").select("id").eq("slug", data.domainSlug).maybeSingle();
      if (d) q = q.eq("domain_id", d.id);
    }
    const { data: rows } = await q;
    return rows ?? [];
  });

// -------- Authenticated --------

export const listMyProgress = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase.from("academy_progress")
      .select("lesson_id, status, progress_pct, mastery_pct, completed_at, updated_at")
      .eq("user_id", context.userId).order("updated_at", { ascending: false });
    return data ?? [];
  });

export const upsertProgress = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { lessonId: string; progressPct: number; status?: "in_progress" | "completed"; timeSpentS?: number; masteryPct?: number }) => d)
  .handler(async ({ data, context }) => {
    const status = data.status ?? (data.progressPct >= 100 ? "completed" : "in_progress");
    const { error } = await context.supabase.from("academy_progress").upsert({
      user_id: context.userId,
      lesson_id: data.lessonId,
      status,
      progress_pct: Math.max(0, Math.min(100, Math.round(data.progressPct))),
      time_spent_s: Math.max(0, Math.round(data.timeSpentS ?? 0)),
      mastery_pct: Math.max(0, Math.min(100, Math.round(data.masteryPct ?? 0))),
      completed_at: status === "completed" ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id,lesson_id" });
    if (error) throw new Error(error.message);

    // Streak bump on completion
    if (status === "completed") {
      const today = new Date().toISOString().slice(0, 10);
      const { data: streak } = await context.supabase.from("academy_streaks").select("*").eq("user_id", context.userId).maybeSingle();
      const last = streak?.last_practiced_on ?? null;
      let cur = streak?.current_streak ?? 0;
      if (last === today) { /* no change */ }
      else {
        const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        cur = last === yesterday ? cur + 1 : 1;
      }
      const longest = Math.max(streak?.longest_streak ?? 0, cur);
      await context.supabase.from("academy_streaks").upsert({
        user_id: context.userId,
        current_streak: cur,
        longest_streak: longest,
        last_practiced_on: today,
        xp: (streak?.xp ?? 0) + 10,
        updated_at: new Date().toISOString(),
      });
    }
    return { ok: true };
  });

// Admin-configurable mastery rule. Stored on academy_lessons.mastery_rule.
// - required_indices: null => every scenario in the lesson counts once
//                     array => only those scenario indices count
// - min_correct_attempts: how many correct answers are needed per counted scenario
type MasteryRule = { required_indices: number[] | null; min_correct_attempts: number };

function normalizeRule(raw: unknown): MasteryRule {
  const r = (raw ?? {}) as Partial<MasteryRule>;
  const idx = Array.isArray(r.required_indices)
    ? Array.from(new Set(r.required_indices.filter((n) => Number.isFinite(n) && n >= 0).map((n) => Math.floor(n as number)))).sort((a, b) => a - b)
    : null;
  const min = Math.max(1, Math.floor(Number(r.min_correct_attempts ?? 1)) || 1);
  return { required_indices: idx, min_correct_attempts: min };
}

function evaluateRule(
  rule: MasteryRule,
  attempts: Record<string, number>,
  scenariosTotal: number,
): { ready: boolean; countedIndices: number[]; satisfiedCount: number } {
  const counted = (rule.required_indices ?? Array.from({ length: scenariosTotal }, (_, i) => i))
    .filter((i) => i < scenariosTotal);
  const satisfied = counted.filter((i) => (attempts[String(i)] ?? 0) >= rule.min_correct_attempts);
  return { ready: counted.length > 0 && satisfied.length === counted.length, countedIndices: counted, satisfiedCount: satisfied.length };
}

// Record that a learner correctly answered a specific scenario. The lesson's
// admin-configured mastery_rule decides which scenarios count and how many
// correct attempts are required per scenario to mark the lesson as Ready.
export const recordScenarioMastery = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { lessonId: string; scenarioIndex: number; scenariosTotal: number }) => d)
  .handler(async ({ data, context }) => {
    const idx = Math.max(0, Math.floor(data.scenarioIndex));
    const total = Math.max(1, Math.floor(data.scenariosTotal));

    const { data: lessonRow } = await context.supabase
      .from("academy_lessons")
      .select("mastery_rule")
      .eq("id", data.lessonId)
      .maybeSingle();
    const rule = normalizeRule((lessonRow as { mastery_rule?: unknown } | null)?.mastery_rule);

    const { data: existing } = await context.supabase
      .from("academy_progress")
      .select("scenarios_mastered, mastered_at, status, progress_pct, scenario_attempts")
      .eq("user_id", context.userId)
      .eq("lesson_id", data.lessonId)
      .maybeSingle();

    const prevAttempts = (existing?.scenario_attempts ?? {}) as Record<string, number>;
    const attempts: Record<string, number> = { ...prevAttempts };
    attempts[String(idx)] = Math.max(0, Number(attempts[String(idx)] ?? 0)) + 1;

    const evalResult = evaluateRule(rule, attempts, total);
    const legacyMastered = Array.from(new Set([...(Array.isArray(existing?.scenarios_mastered) ? existing!.scenarios_mastered as number[] : []), idx])).filter((n) => n < total).sort((a, b) => a - b);
    const masteredAt = evalResult.ready ? (existing?.mastered_at ?? new Date().toISOString()) : null;

    const { error } = await context.supabase.from("academy_progress").upsert({
      user_id: context.userId,
      lesson_id: data.lessonId,
      status: existing?.status ?? "in_progress",
      progress_pct: existing?.progress_pct ?? 0,
      scenarios_mastered: legacyMastered,
      scenarios_total: total,
      scenario_attempts: attempts,
      mastered_at: masteredAt,
      updated_at: new Date().toISOString(),
    }, { onConflict: "user_id,lesson_id" });
    if (error) throw new Error(error.message);
    return {
      mastered: evalResult.ready,
      masteredAt,
      total,
      rule,
      countedIndices: evalResult.countedIndices,
      satisfiedCount: evalResult.satisfiedCount,
      attempts,
    };
  });

export const getLessonMastery = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { lessonId: string }) => d)
  .handler(async ({ data, context }) => {
    const [{ data: row }, { data: lessonRow }] = await Promise.all([
      context.supabase
        .from("academy_progress")
        .select("scenarios_mastered, scenarios_total, scenario_attempts, mastered_at")
        .eq("user_id", context.userId)
        .eq("lesson_id", data.lessonId)
        .maybeSingle(),
      context.supabase
        .from("academy_lessons")
        .select("mastery_rule")
        .eq("id", data.lessonId)
        .maybeSingle(),
    ]);
    const rule = normalizeRule((lessonRow as { mastery_rule?: unknown } | null)?.mastery_rule);
    const attempts = (row?.scenario_attempts ?? {}) as Record<string, number>;
    const total = row?.scenarios_total ?? 0;
    const evalResult = evaluateRule(rule, attempts, total || (rule.required_indices?.length ?? 0));
    const legacy = Array.isArray(row?.scenarios_mastered) ? (row!.scenarios_mastered as number[]) : [];
    return {
      scenariosMastered: legacy,
      scenariosTotal: row?.scenarios_total ?? null,
      masteredAt: row?.mastered_at ?? null,
      rule,
      attempts,
      countedIndices: evalResult.countedIndices,
      satisfiedCount: evalResult.satisfiedCount,
    };
  });


export const toggleBookmark = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { lessonId: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: existing } = await context.supabase.from("academy_bookmarks")
      .select("lesson_id").eq("user_id", context.userId).eq("lesson_id", data.lessonId).maybeSingle();
    if (existing) {
      await context.supabase.from("academy_bookmarks").delete().eq("user_id", context.userId).eq("lesson_id", data.lessonId);
      return { bookmarked: false };
    }
    await context.supabase.from("academy_bookmarks").insert({ user_id: context.userId, lesson_id: data.lessonId });
    return { bookmarked: true };
  });

export const listMyBookmarks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase.from("academy_bookmarks")
      .select("lesson_id, created_at, academy_lessons(id, slug, title, summary, domain_id)")
      .eq("user_id", context.userId).order("created_at", { ascending: false });
    return data ?? [];
  });

export const getMyStreak = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase.from("academy_streaks").select("*").eq("user_id", context.userId).maybeSingle();
    return data ?? { current_streak: 0, longest_streak: 0, last_practiced_on: null, xp: 0 };
  });

export const saveNote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { lessonId: string; body: string; id?: string }) => d)
  .handler(async ({ data, context }) => {
    if (data.id) {
      const { error } = await context.supabase.from("academy_notes")
        .update({ body_md: data.body, updated_at: new Date().toISOString() })
        .eq("id", data.id).eq("user_id", context.userId);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await context.supabase.from("academy_notes")
      .insert({ user_id: context.userId, lesson_id: data.lessonId, body_md: data.body })
      .select("id").single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

export const listNotesForLesson = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { lessonId: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: rows } = await context.supabase.from("academy_notes")
      .select("id, body_md, updated_at").eq("user_id", context.userId).eq("lesson_id", data.lessonId)
      .order("updated_at", { ascending: false });
    return rows ?? [];
  });

// -------- AI Guide --------

export const askAcademyGuide = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { lessonId?: string; domainSlug?: string; question: string }) => d)
  .handler(async ({ data, context }) => {
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("AI Guide unavailable");
    const question = (data.question ?? "").toString().trim().slice(0, 2000);
    if (!question) throw new Error("Question required");
    const { checkRateLimit } = await import("./rate-limit.server");
    const allowed = await checkRateLimit(`academy_guide:${context.userId}`, 20, 3600);
    if (!allowed) throw new Error("Too many requests. Please try again in a bit.");
    let corpus = "";
    if (data.lessonId) {
      const { data: l } = await context.supabase.from("academy_lessons")
        .select("title, summary, body_md").eq("id", data.lessonId).maybeSingle();
      if (l) corpus = `# ${l.title}\n${l.summary ?? ""}\n\n${l.body_md ?? ""}`;
    } else if (data.domainSlug) {
      const { data: d } = await context.supabase.from("academy_domains").select("id").eq("slug", data.domainSlug).maybeSingle();
      if (d) {
        const { data: lessons } = await context.supabase.from("academy_lessons")
          .select("title, summary").eq("domain_id", d.id).eq("is_published", true).limit(20);
        corpus = (lessons ?? []).map((l) => `- ${l.title}: ${l.summary ?? ""}`).join("\n");
      }
    }
    const { createLovableAiGatewayProvider } = await import("./ai-gateway.server");
    const { generateText } = await import("ai");
    const gateway = createLovableAiGatewayProvider(key);
    const { text } = await generateText({
      model: gateway("google/gemini-3.5-flash"),
      system:
        "You are the KAfrequency Academy Guide. Answer clearly and briefly from the provided lesson corpus. If unsure, say so. Stay grounded, kind, and precise. Never invent facts.",
      prompt: `LESSON CORPUS:\n${corpus}\n\nUSER QUESTION:\n${question}`,
    });
    return { answer: text };
  });

// -------- Journal --------

export const listJournalEntries = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { q?: string; tag?: string } = {}) => d)
  .handler(async ({ data, context }) => {
    let q = context.supabase
      .from("academy_journal_entries")
      .select("id, title, body_md, tags, mood, lesson_id, created_at, updated_at")
      .eq("user_id", context.userId)
      .order("updated_at", { ascending: false })
      .limit(100);
    if (data?.q?.trim()) q = q.ilike("body_md", `%${data.q}%`);
    if (data?.tag) q = q.contains("tags", [data.tag]);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const saveJournalEntry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id?: string; title?: string; body: string; tags?: string[]; mood?: string; lessonId?: string }) => d)
  .handler(async ({ data, context }) => {
    if (data.id) {
      const { error } = await context.supabase
        .from("academy_journal_entries")
        .update({
          title: data.title ?? null,
          body_md: data.body,
          tags: data.tags ?? [],
          mood: data.mood ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", data.id).eq("user_id", context.userId);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await context.supabase
      .from("academy_journal_entries")
      .insert({
        user_id: context.userId,
        title: data.title ?? null,
        body_md: data.body,
        tags: data.tags ?? [],
        mood: data.mood ?? null,
        lesson_id: data.lessonId ?? null,
      })
      .select("id").single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

export const deleteJournalEntry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("academy_journal_entries")
      .delete().eq("id", data.id).eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// -------- Knowledge Graph --------

export const getKnowledgeGraph = createServerFn({ method: "GET" })
  .inputValidator((d: { domainSlug: string }) => d)
  .handler(async ({ data }) => {
    const supa = publicClient();
    const { data: domain } = await supa.from("academy_domains").select("id").eq("slug", data.domainSlug).maybeSingle();
    if (!domain) return { nodes: [], edges: [] };
    const { data: lessons } = await supa.from("academy_lessons")
      .select("id, slug, title, difficulty, tier, prerequisites, related_ids, category_id")
      .eq("domain_id", domain.id).eq("is_published", true).limit(500);
    const nodes = (lessons ?? []).map((l) => ({
      id: l.id, slug: l.slug, title: l.title, difficulty: l.difficulty, tier: l.tier, category: l.category_id,
    }));
    const idSet = new Set(nodes.map((n) => n.id));
    const edges: { source: string; target: string; kind: "prereq" | "related" }[] = [];
    for (const l of lessons ?? []) {
      for (const p of (l.prerequisites as string[] | null) ?? []) {
        if (idSet.has(p)) edges.push({ source: p, target: l.id, kind: "prereq" });
      }
      for (const r of (l.related_ids as string[] | null) ?? []) {
        if (idSet.has(r)) edges.push({ source: l.id, target: r, kind: "related" });
      }
    }
    return { nodes, edges };
  });

// -------- Daily Practice --------

export const getDailyPractice = createServerFn({ method: "GET" })
  .inputValidator((d: { domainSlug?: string } = {}) => d)
  .handler(async ({ data }) => {
    const supa = publicClient();
    // Deterministic pick by day-of-year so everyone gets the same daily lesson.
    const today = new Date();
    const start = Date.UTC(today.getUTCFullYear(), 0, 0);
    const doy = Math.floor((today.getTime() - start) / 86400000);
    let q = supa.from("academy_lessons")
      .select("id, slug, title, subtitle, summary, difficulty, tier, est_minutes, hero_image_url, prompts, domain_id")
      .eq("is_published", true);
    if (data?.domainSlug) {
      const { data: d } = await supa.from("academy_domains").select("id").eq("slug", data.domainSlug).maybeSingle();
      if (d) q = q.eq("domain_id", d.id);
    }
    const { data: rows } = await q.order("slug").limit(200);
    if (!rows || !rows.length) return null;
    const lesson = rows[doy % rows.length];
    const { data: domain } = await supa.from("academy_domains").select("slug, name").eq("id", lesson.domain_id).maybeSingle();
    return { lesson, domain, dayIndex: doy };
  });

// ---------- Publish lifecycle (Slice B) ----------

type PublishTable = "academy_lessons" | "academy_paths" | "academy_domains" | "kaf_lessons";
type Status = "draft" | "review" | "published";

export const previewPublishReadiness = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { table: PublishTable; id: string }) => d)
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("@/lib/shop.server");
    await assertAdmin(context as never);
    const supa = context.supabase;
    const reasons: string[] = [];

    if (data.table === "academy_lessons") {
      const { data: row } = await supa.from("academy_lessons").select("id, slug, body_md").eq("id", data.id).maybeSingle();
      if (!row) return { ok: false, reasons: ["lesson not found"] };
      const { count } = await supa.from("academy_lesson_blocks").select("id", { count: "exact", head: true }).eq("lesson_id", data.id);
      if ((row.body_md?.length ?? 0) < 200 && (count ?? 0) === 0) {
        reasons.push("Body must be ≥ 200 characters or at least one lesson block must exist.");
      }
    } else if (data.table === "academy_paths") {
      const { count } = await supa.from("academy_path_lessons").select("id", { count: "exact", head: true }).eq("path_id", data.id);
      if ((count ?? 0) === 0) reasons.push("Path needs at least one associated lesson.");
    } else if (data.table === "academy_domains") {
      const { count } = await supa.from("academy_lessons").select("id", { count: "exact", head: true }).eq("domain_id", data.id);
      if ((count ?? 0) === 0) reasons.push("Domain needs at least one lesson.");
    } else if (data.table === "kaf_lessons") {
      const { data: row } = await supa.from("kaf_lessons").select("id, slug, body").eq("id", data.id).maybeSingle();
      if (!row) return { ok: false, reasons: ["lesson not found"] };
      const bodyLen = JSON.stringify(row.body ?? []).length;
      if (bodyLen < 220) reasons.push("Body must contain meaningful content.");
    }
    return { ok: reasons.length === 0, reasons };
  });

export const setPublishStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { table: PublishTable; id: string; status: Status; reviewNotes?: string }) => d)
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("@/lib/shop.server");
    await assertAdmin(context as never);
    const patch: Record<string, unknown> = { status: data.status };
    if (data.status === "review") patch.submitted_at = new Date().toISOString();
    if (data.status === "published") {
      patch.reviewed_at = new Date().toISOString();
      patch.reviewed_by = context.userId;
    }
    if (data.reviewNotes !== undefined) patch.review_notes = data.reviewNotes;
    const { error } = await (context.supabase.from(data.table) as unknown as { update: (p: Record<string, unknown>) => { eq: (col: string, val: string) => Promise<{ error: { message: string } | null }> } }).update(patch).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- Assessment Runner (server-scored quiz + reflections) ----------

type QuizItem = { q: string; choices?: string[]; options?: string[]; answer: number; explanation?: string; explain?: string };
type QuizShape = QuizItem[] | { questions?: QuizItem[]; items?: QuizItem[] };
type PromptItem = string | { prompt?: string; text?: string };
type PromptsShape = PromptItem[] | { reflection?: (string | PromptItem)[]; practice?: string };

function extractQuizItems(quiz: unknown): QuizItem[] {
  if (!quiz) return [];
  if (Array.isArray(quiz)) return quiz as QuizItem[];
  const q = quiz as { questions?: QuizItem[]; items?: QuizItem[] };
  return (q.questions ?? q.items ?? []) as QuizItem[];
}

function extractPromptTexts(prompts: unknown): string[] {
  if (!prompts) return [];
  if (Array.isArray(prompts)) {
    return prompts
      .map((p) => (typeof p === "string" ? p : (p as { prompt?: string; text?: string })?.prompt ?? (p as { text?: string })?.text ?? ""))
      .filter(Boolean);
  }
  const o = prompts as { reflection?: (string | { prompt?: string })[] };
  return (o.reflection ?? [])
    .map((p) => (typeof p === "string" ? p : p?.prompt ?? ""))
    .filter(Boolean);
}

export const submitLessonAssessment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { lessonId: string; answers: number[]; reflections?: string[]; passThreshold?: number }) => d)
  .handler(async ({ data, context }) => {
    const { data: lesson, error } = await context.supabase
      .from("academy_lessons")
      .select("id, slug, title, quiz, prompts, next_lesson_slugs, domain_id")
      .eq("id", data.lessonId).eq("is_published", true).maybeSingle();
    if (error) throw new Error(error.message);
    if (!lesson) throw new Error("Lesson not found");

    const items = extractQuizItems(lesson.quiz as QuizShape | null);
    if (!items.length) throw new Error("This lesson has no assessment.");

    // Score
    const results = items.map((item, i) => {
      const picked = data.answers[i];
      const correct = typeof picked === "number" && picked === item.answer;
      return {
        q: item.q,
        picked: picked ?? -1,
        correct,
        answer: item.answer,
        explanation: item.explanation ?? item.explain ?? "",
      };
    });
    const correctCount = results.filter((r) => r.correct).length;
    const masteryPct = Math.round((correctCount / items.length) * 100);
    const threshold = Math.max(0, Math.min(100, data.passThreshold ?? 70));
    const passed = masteryPct >= threshold;

    // Persist reflections as one journal entry linked to the lesson
    const promptTexts = extractPromptTexts(lesson.prompts as PromptsShape | null);
    const cleanReflections = (data.reflections ?? []).map((r) => (r ?? "").trim());
    const hasReflection = cleanReflections.some((r) => r.length > 0);
    if (hasReflection) {
      const body = promptTexts
        .map((p, i) => `**${p}**\n\n${cleanReflections[i] ?? ""}`.trim())
        .filter((b) => b.length > 0)
        .join("\n\n---\n\n");
      await context.supabase.from("academy_journal_entries").insert({
        user_id: context.userId,
        lesson_id: lesson.id,
        title: `Reflections · ${lesson.title}`,
        body_md: body,
        tags: ["assessment", "reflection"],
      });
    }

    // Upsert progress (mastery always recorded; status=completed only on pass)
    const nowIso = new Date().toISOString();
    const status = passed ? "completed" : "in_progress";
    await context.supabase.from("academy_progress").upsert({
      user_id: context.userId,
      lesson_id: lesson.id,
      status,
      progress_pct: passed ? 100 : Math.max(50, masteryPct),
      mastery_pct: masteryPct,
      completed_at: passed ? nowIso : null,
      updated_at: nowIso,
    }, { onConflict: "user_id,lesson_id" });

    // Bump streak on pass (mirror upsertProgress)
    if (passed) {
      const today = nowIso.slice(0, 10);
      const { data: streak } = await context.supabase.from("academy_streaks").select("*").eq("user_id", context.userId).maybeSingle();
      const last = streak?.last_practiced_on ?? null;
      let cur = streak?.current_streak ?? 0;
      if (last !== today) {
        const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        cur = last === yesterday ? cur + 1 : 1;
      }
      const longest = Math.max(streak?.longest_streak ?? 0, cur);
      await context.supabase.from("academy_streaks").upsert({
        user_id: context.userId,
        current_streak: cur,
        longest_streak: longest,
        last_practiced_on: today,
        xp: (streak?.xp ?? 0) + (passed ? 25 : 5),
        updated_at: nowIso,
      });
    }

    return {
      passed,
      masteryPct,
      correctCount,
      total: items.length,
      threshold,
      results,
      nextLessonSlugs: (lesson.next_lesson_slugs as string[] | null) ?? [],
      reflectionsSaved: hasReflection,
    };
  });
